import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import Spinner from "../../../shared/ui/Loading/Spinner";
import Field from "../../../shared/ui/Field/Field";
import Select from "../../../shared/ui/Field/Select";
import TextInput from "../../../shared/ui/Field/TextInput";
import {
  createPropertyPresentation,
  fetchPropertyPresentationAnalytics,
  listPropertyPresentations,
  reactivatePropertyPresentation,
  revokePropertyPresentation,
  rotatePropertyPresentation,
  updatePropertyPresentation,
  PRESENTATION_WRITE_FIELDS,
} from "../api/managerApi";
import {
  createCompanyPresentation,
  fetchCompanyPresentationAnalytics,
  fetchCompanyPresentationAudit,
  handoverCompanyPresentation,
  listCompanyPresentations,
  maintainCompanyPresentationContact,
  reactivateCompanyPresentation,
  revokeCompanyPresentation,
  rotateCompanyPresentation,
  unwrapPresentationList,
  updateCompanyPresentation,
} from "../api/corporateManagerApi";
import { parseDrfFieldErrors, messageFromError } from "../../../shared/api/drfErrors";
import { pickPublicProjection } from "../lib/publicProjection";
import {
  PRICE_MODE_LABELS,
  STATUS_LABELS,
  formatDate,
  formatDateTime,
  presentationRequiresExpiryRestore,
  presentationStatus,
} from "../lib/labels";
import {
  DEFAULT_EXPIRY_PRESET,
  EXPIRY_PRESETS,
  buildExpiredRestorePayload,
} from "../lib/expiry";
import { copyPublicUrl, isShareCancellation, sharePublicUrl } from "../lib/copyShare";
import { maskPresentationPublicUrl } from "../lib/publicUrlDisplay";
import {
  canCap,
  clearUnsafeClientProjection,
  corporateAuditLabel,
  filterPresentationsForProperty,
  isCompanyManagerMode,
  isCorporateConflictError,
  messageFromCorporateError,
  toCorporateContactBody,
  toCorporateWriteBody,
  withCorporateConcurrency,
} from "../lib/corporatePresentationMode";
import { memberDisplayName } from "../../companyWorkspace/lib/companyLabels";
import PresentationEditorForm, {
  buildEditorDraft,
  buildWritePayload,
  validateDraft,
} from "./PresentationEditorForm";
import { containTabKey, stageIndexForFieldErrors } from "../lib/wizard";
import PresentationReviewSummary from "./PresentationReviewSummary";
import PublicPresentationRenderer from "./PublicPresentationRenderer";
import PresentationConfirmDialog from "./PresentationConfirmDialog";
import PresentationToggle from "./PresentationToggle";

const PresentationQrPanel = lazy(() => import("./PresentationQrPanel"));

function emptyManagerUi() {
  return {
    view: "list",
    items: [],
    loadState: "idle",
    loadError: null,
    draft: null,
    editingShare: null,
    activeShare: null,
    fieldErrors: {},
    formError: null,
    busy: false,
    copied: false,
    shareFeedback: null,
    confirm: null,
    qrOpen: false,
    previewShare: null,
    reviewPayload: null,
    loadedPropertyId: null,
    editorStage: 0,
    restoreExpiryPreset: DEFAULT_EXPIRY_PRESET,
    restoreCustomExpiresAt: "",
    restoreExpiryError: null,
    handoverUserId: "",
    auditRows: [],
    auditState: "idle",
    contactDraft: null,
  };
}

function corporateActionAllowed(companyMode, share, key) {
  if (!companyMode) return true;
  return canCap(share, key);
}

function revokedLocalShare(share) {
  return {
    ...share,
    is_active: false,
    is_active_now: false,
    revoked_at: share?.revoked_at || new Date().toISOString(),
  };
}

function PresentationUrlRow({ url }) {
  const masked = maskPresentationPublicUrl(url);
  return (
    <div className="presentation-url-row">
      <input
        readOnly
        value={masked}
        aria-label="Публічне посилання (токен приховано)"
        data-testid="presentation-public-url"
      />
    </div>
  );
}

function PresentationBrandSnapshot({ share }) {
  if (!share) return null;
  const name = share.brand_name_snapshot;
  const logo = share.brand_logo_url;
  if (!name && !logo) return null;
  return (
    <div
      className="presentation-manager-card__brand"
      data-testid="presentation-brand-snapshot"
    >
      {logo ? (
        <img
          src={logo}
          alt=""
          className="presentation-manager-card__brand-logo"
          data-testid="presentation-brand-logo"
        />
      ) : null}
      <span className="presentation-manager-list__meta">
        Бренд (зафіксовано): {name || "—"}
      </span>
    </div>
  );
}

function PresentationOwnerToolbar({
  share,
  copied,
  onCopy,
  onShare,
  onQr,
  onEdit,
  onList,
}) {
  if (!share || !share.public_url) return null;
  return (
    <div className="presentation-owner-toolbar" data-testid="presentation-owner-toolbar">
      <a
        className="action-btn action-btn--primary"
        href={share.public_url}
        target="_blank"
        rel="noopener noreferrer"
      >
        Відкрити публічну
      </a>
      <button type="button" className="action-btn" onClick={() => onCopy(share)}>
        {copied ? "Посилання скопійовано" : "Копіювати"}
      </button>
      <button type="button" className="action-btn" onClick={() => onShare(share)}>
        Надіслати
      </button>
      <button type="button" className="action-btn" onClick={onQr}>
        QR
      </button>
      {onEdit ? (
        <button type="button" className="action-btn" onClick={onEdit}>
          Редагувати
        </button>
      ) : null}
      <button type="button" className="action-btn" onClick={onList}>
        До списку
      </button>
    </div>
  );
}

function PresentationManagerModal({
  open,
  property,
  ownerContactPrefill,
  onClose,
  partnerPriceLocked = false,
  managerContext = null,
}) {
  const companyMode = isCompanyManagerMode(managerContext);
  const companyId = companyMode ? managerContext.companyId : null;
  const [view, setView] = useState("list");
  const [items, setItems] = useState([]);
  const [loadState, setLoadState] = useState("idle");
  const [loadError, setLoadError] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editingShare, setEditingShare] = useState(null);
  const [activeShare, setActiveShare] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareFeedback, setShareFeedback] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [previewShare, setPreviewShare] = useState(null);
  const [reviewPayload, setReviewPayload] = useState(null);
  const [loadedPropertyId, setLoadedPropertyId] = useState(null);
  const [editorStage, setEditorStage] = useState(0);
  const [restoreExpiryPreset, setRestoreExpiryPreset] = useState(DEFAULT_EXPIRY_PRESET);
  const [restoreCustomExpiresAt, setRestoreCustomExpiresAt] = useState("");
  const [restoreExpiryError, setRestoreExpiryError] = useState(null);
  const [handoverUserId, setHandoverUserId] = useState("");
  const [auditRows, setAuditRows] = useState([]);
  const [auditState, setAuditState] = useState("idle");
  const [contactDraft, setContactDraft] = useState(null);
  const [contactFieldErrors, setContactFieldErrors] = useState({});
  const busyRef = useRef(false);
  const copiedTimerRef = useRef(null);
  const sessionRef = useRef(0);
  const listOpRef = useRef(0);
  const mutationOpRef = useRef(0);
  const analyticsOpRef = useRef(0);
  const shareOpRef = useRef(0);
  const restoreFocusRef = useRef(null);
  const closeBtnRef = useRef(null);
  const modalRef = useRef(null);
  const propertyId = property && property.id;
  const propertyIdRef = useRef(propertyId);
  propertyIdRef.current = propertyId;
  const managerContextRef = useRef(managerContext);
  managerContextRef.current = managerContext;

  const clearCopiedTimer = () => {
    if (copiedTimerRef.current) {
      window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = null;
    }
  };

  const resetSessionUi = () => {
    const next = emptyManagerUi();
    setView(next.view);
    setItems(next.items);
    setLoadState(next.loadState);
    setLoadError(next.loadError);
    setDraft(next.draft);
    setEditingShare(next.editingShare);
    setActiveShare(next.activeShare);
    setFieldErrors(next.fieldErrors);
    setFormError(next.formError);
    setBusy(next.busy);
    setCopied(next.copied);
    setShareFeedback(next.shareFeedback);
    setConfirm(next.confirm);
    setQrOpen(next.qrOpen);
    setPreviewShare(next.previewShare);
    setReviewPayload(next.reviewPayload);
    setLoadedPropertyId(next.loadedPropertyId);
    setEditorStage(next.editorStage);
    setRestoreExpiryPreset(next.restoreExpiryPreset);
    setRestoreCustomExpiresAt(next.restoreCustomExpiresAt);
    setRestoreExpiryError(next.restoreExpiryError);
    setHandoverUserId(next.handoverUserId);
    setAuditRows(next.auditRows);
    setAuditState(next.auditState);
    setContactDraft(next.contactDraft);
    setContactFieldErrors({});
    busyRef.current = false;
    clearCopiedTimer();
  };

  const beginSession = () => {
    sessionRef.current += 1;
    listOpRef.current += 1;
    mutationOpRef.current += 1;
    analyticsOpRef.current += 1;
    shareOpRef.current += 1;
    resetSessionUi();
    return sessionRef.current;
  };

  const isCurrent = (session, opRef, op) =>
    sessionRef.current === session && opRef.current === op;

  const isShareCurrent = (session, property, op) =>
    sessionRef.current === session &&
    propertyIdRef.current === property &&
    shareOpRef.current === op;

  const normalizeCorporateRows = (data) =>
    filterPresentationsForProperty(
      unwrapPresentationList(data).map(clearUnsafeClientProjection),
      propertyIdRef.current,
    );

  const refetchCorporateList = async (session) => {
    const ctx = managerContextRef.current;
    if (!isCompanyManagerMode(ctx) || !propertyIdRef.current) return;
    const listOp = listOpRef.current + 1;
    listOpRef.current = listOp;
    try {
      const data = await listCompanyPresentations(ctx.companyId, { property_id: propertyIdRef.current });
      if (!isCurrent(session, listOpRef, listOp)) return;
      setItems(normalizeCorporateRows(data));
      setLoadedPropertyId(propertyIdRef.current);
      setLoadState("loaded");
      setLoadError(null);
    } catch (error) {
      if (!isCurrent(session, listOpRef, listOp)) return;
      if (error && error.code === "ERR_CANCELED") return;
      setLoadError("Не вдалося завантажити презентації.");
      setLoadState("error");
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    restoreFocusRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const appRoot = document.getElementById("root");
    const previousHadInert = appRoot ? appRoot.hasAttribute("inert") : false;
    if (appRoot) appRoot.setAttribute("inert", "");
    closeBtnRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (appRoot) {
        if (previousHadInert) appRoot.setAttribute("inert", "");
        else appRoot.removeAttribute("inert");
      }
      const restore = restoreFocusRef.current;
      if (restore && typeof restore.focus === "function") {
        restore.focus();
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      const lightbox = document.querySelector('[data-testid="presentation-lightbox"]');
      const comboboxOpen = document.querySelector(
        '[data-testid="presentation-client-combobox"][data-open="true"]',
      );
      const nested = modalRef.current
        ? modalRef.current.querySelector(".presentation-modal--nested")
        : document.querySelector(".presentation-modal--nested");
      const topmost = lightbox || nested || modalRef.current;
      if (event.key === "Tab") {
        containTabKey(event, topmost);
        return;
      }
      if (event.key !== "Escape") return;
      if (confirm || qrOpen) return;
      if (lightbox) return;
      if (comboboxOpen) return;
      event.preventDefault();
      onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, confirm, qrOpen, onClose]);

  useEffect(() => {
    return () => {
      sessionRef.current += 1;
      listOpRef.current += 1;
      mutationOpRef.current += 1;
      analyticsOpRef.current += 1;
      shareOpRef.current += 1;
      busyRef.current = false;
      clearCopiedTimer();
    };
  }, []);

  useEffect(() => {
    const session = beginSession();
    if (!open || !propertyId) {
      return undefined;
    }
    if (companyMode && !companyId) {
      setLoadError("Немає контексту компанії для презентацій.");
      setLoadState("error");
      return undefined;
    }
    const listOp = listOpRef.current + 1;
    listOpRef.current = listOp;
    setLoadState("loading");
    setLoadError(null);
    setItems([]);
    const controller = new AbortController();
    const listPromise = companyMode
      ? listCompanyPresentations(companyId, { property_id: propertyId }, { signal: controller.signal }).then(
          (data) => normalizeCorporateRows(data),
        )
      : listPropertyPresentations(propertyId, { signal: controller.signal }).then(
          (data) => (Array.isArray(data) ? data : []),
        );
    listPromise
      .then((rows) => {
        if (!isCurrent(session, listOpRef, listOp)) return;
        setItems(rows);
        setLoadedPropertyId(propertyId);
        setLoadState("loaded");
      })
      .catch((error) => {
        if (!isCurrent(session, listOpRef, listOp)) return;
        if (error && error.code === "ERR_CANCELED") return;
        setLoadError("Не вдалося завантажити презентації.");
        setLoadState("error");
      });
    return () => controller.abort();
    // beginSession is session-local and must run on open/property identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, propertyId, companyMode, companyId]);

  if (!open) return null;

  const replaceItem = (nextShare) => {
    const safe = companyMode ? clearUnsafeClientProjection(nextShare) : nextShare;
    setItems((current) => {
      const index = current.findIndex((row) => row.id === safe.id);
      if (index === -1) return [safe, ...current];
      const copy = [...current];
      copy[index] = safe;
      return copy;
    });
    setActiveShare(safe);
  };

  const canCreate = companyMode
    ? Boolean(managerContext?.canCreate)
    : Boolean(property?.can_create_presentation);

  const memberOptions = (managerContext?.members || [])
    .map((member) => {
      const userId = member?.user?.id;
      if (userId == null) return null;
      return {
        value: String(userId),
        label: memberDisplayName(member),
      };
    })
    .filter(Boolean);

  const startCreate = () => {
    if (!canCreate) return;
    setEditingShare(null);
    setActiveShare(null);
    setDraft(buildEditorDraft({
      property,
      ownerContactPrefill,
      priceLocked: partnerPriceLocked,
    }));
    setFieldErrors({});
    setFormError(null);
    setReviewPayload(null);
    setEditorStage(0);
    setView("editor");
  };

  const startEdit = (share) => {
    if (!corporateActionAllowed(companyMode, share, "can_update")) return;
    setEditingShare(share);
    setActiveShare(share);
    setDraft(buildEditorDraft({
      share,
      property,
      ownerContactPrefill,
      priceLocked: partnerPriceLocked,
    }));
    setFieldErrors({});
    setFormError(null);
    setReviewPayload(null);
    setEditorStage(0);
    setView("editor");
  };

  const enterReview = () => {
    const errors = validateDraft(draft);
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setEditorStage(stageIndexForFieldErrors(errors));
      return;
    }
    setFieldErrors({});
    setFormError(null);
    setReviewPayload(buildWritePayload(draft));
    setView("review");
  };

  const handleSave = async () => {
    if (busyRef.current) return;
    const payload = reviewPayload;
    if (!payload) return;
    const session = sessionRef.current;
    const op = mutationOpRef.current + 1;
    mutationOpRef.current = op;
    busyRef.current = true;
    setBusy(true);
    setFormError(null);
    try {
      let saved;
      if (companyMode) {
        const ctx = managerContextRef.current;
        const body = toCorporateWriteBody({
          personalPayload: payload,
          propertyId,
          managerContext: ctx,
          share: editingShare,
          confirmCompanyFallback: Boolean(draft?.confirm_company_fallback),
        });
        saved = editingShare
          ? await updateCompanyPresentation(ctx.companyId, editingShare.id, body)
          : await createCompanyPresentation(ctx.companyId, body);
      } else {
        saved = editingShare
          ? await updatePropertyPresentation(propertyId, editingShare.id, payload)
          : await createPropertyPresentation(propertyId, payload);
      }
      if (!isCurrent(session, mutationOpRef, op)) return;
      replaceItem(saved);
      setPreviewShare(saved);
      setCopied(false);
      setShareFeedback(null);
      setQrOpen(false);
      setView("share");
    } catch (error) {
      if (!isCurrent(session, mutationOpRef, op)) return;
      if (companyMode && isCorporateConflictError(error)) {
        setFormError(messageFromCorporateError(error));
        setReviewPayload(null);
        setView("list");
        await refetchCorporateList(session);
        return;
      }
      const parsed = parseDrfFieldErrors(error, PRESENTATION_WRITE_FIELDS);
      const nextFields = { ...parsed };
      const form = nextFields._form || null;
      delete nextFields._form;
      setFieldErrors(nextFields);
      setFormError(
        form
          || (Object.keys(nextFields).length
            ? null
            : (companyMode ? messageFromCorporateError(error) : messageFromError(error))),
      );
      setReviewPayload(null);
      setEditorStage(stageIndexForFieldErrors(nextFields));
      setView("editor");
    } finally {
      if (isCurrent(session, mutationOpRef, op)) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };

  const beginShareOperation = () => {
    clearCopiedTimer();
    setCopied(false);
    setShareFeedback(null);
    const session = sessionRef.current;
    const property = propertyIdRef.current;
    const op = shareOpRef.current + 1;
    shareOpRef.current = op;
    return { session, property, op };
  };

  const markCopied = (session, property, op) => {
    setCopied(true);
    clearCopiedTimer();
    const timer = window.setTimeout(() => {
      if (!isShareCurrent(session, property, op)) return;
      if (copiedTimerRef.current !== timer) return;
      setCopied(false);
      copiedTimerRef.current = null;
    }, 2000);
    copiedTimerRef.current = timer;
  };

  const handleCopy = async (share) => {
    const { session, property, op } = beginShareOperation();
    try {
      await copyPublicUrl(share.public_url);
      if (!isShareCurrent(session, property, op)) return;
      setActiveShare(share);
      markCopied(session, property, op);
    } catch (error) {
      if (!isShareCurrent(session, property, op)) return;
      setShareFeedback(error.message || "Не вдалося скопіювати.");
    }
  };

  const handleShare = async (share) => {
    const { session, property, op } = beginShareOperation();
    try {
      const result = await sharePublicUrl(share.public_url, share.title);
      if (!isShareCurrent(session, property, op)) return;
      setActiveShare(share);
      if (result === "copied") markCopied(session, property, op);
    } catch (error) {
      if (!isShareCurrent(session, property, op)) return;
      if (isShareCancellation(error)) return;
      setShareFeedback(error.message || "Не вдалося надіслати.");
    }
  };

  const beginExpiredRestore = (share) => {
    if (!corporateActionAllowed(companyMode, share, "can_reactivate")) return;
    setRestoreExpiryPreset(DEFAULT_EXPIRY_PRESET);
    setRestoreCustomExpiresAt("");
    setRestoreExpiryError(null);
    setShareFeedback(null);
    setConfirm({
      action: "restore_expired",
      share,
      title: "Поновити презентацію?",
      message: "Публічне посилання знову стане доступним після нового терміну дії.",
      confirmLabel: "Поновити",
    });
  };

  const beginHandover = (share) => {
    if (!corporateActionAllowed(companyMode, share, "can_handover")) return;
    setHandoverUserId("");
    setShareFeedback(null);
    setConfirm({
      action: "handover",
      share,
      title: "Передати відповідальність?",
      message: "Оберіть нового відповідального учасника компанії. Дія явна і незворотна без повторної передачі.",
      confirmLabel: "Передати",
    });
  };

  const beginContactMaintain = (share) => {
    if (!corporateActionAllowed(companyMode, share, "can_maintain_contact")) return;
    setContactDraft({
      contact_phone: share.contact?.phone || "",
      contact_email: share.contact?.email || "",
      include_contact_avatar: Boolean(
        share.include_contact_avatar || share.contact?.avatar_url,
      ),
      // confirm_company_fallback omitted until the user toggles it (F1 omit=preserve).
      fallback_contact_name: share.fallback_contact_name || "",
      fallback_contact_phone: share.fallback_contact_phone || "",
      fallback_contact_email: share.fallback_contact_email || "",
      use_fallback_contact: Boolean(share.use_fallback_contact),
      dirtyFallbackFields: {},
    });
    setContactFieldErrors({});
    setShareFeedback(null);
    setConfirm({
      action: "maintain_contact",
      share,
      title: "Оновити контакт презентації?",
      message: "Контакт корпоративної презентації оновлюється окремо від змісту.",
      confirmLabel: "Зберегти контакт",
    });
  };

  const loadAudit = async (share) => {
    if (!companyMode || !share?.id) return;
    const ctx = managerContextRef.current;
    const session = sessionRef.current;
    setAuditState("loading");
    try {
      const data = await fetchCompanyPresentationAudit(ctx.companyId, share.id);
      if (sessionRef.current !== session) return;
      const rows = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
          ? data.results
          : [];
      setAuditRows(rows);
      setAuditState("loaded");
    } catch (_err) {
      if (sessionRef.current !== session) return;
      setAuditRows([]);
      setAuditState("error");
    }
  };

  const runLifecycle = async (action, share) => {
    if (busyRef.current) return;
    let restoreBody;
    if (action === "restore_expired") {
      const built = buildExpiredRestorePayload(
        restoreExpiryPreset,
        restoreCustomExpiresAt,
      );
      if (built.error) {
        setRestoreExpiryError(built.error);
        return;
      }
      restoreBody = { expires_at: built.expires_at };
      setRestoreExpiryError(null);
    }
    if (action === "handover") {
      if (!handoverUserId) {
        setShareFeedback("Оберіть відповідального учасника.");
        return;
      }
    }
    const session = sessionRef.current;
    const op = mutationOpRef.current + 1;
    mutationOpRef.current = op;
    busyRef.current = true;
    setBusy(true);
    setShareFeedback(null);
    try {
      let next;
      if (companyMode) {
        const ctx = managerContextRef.current;
        if (action === "revoke") {
          await revokeCompanyPresentation(
            ctx.companyId,
            share.id,
            withCorporateConcurrency(ctx, share),
          );
          if (!isCurrent(session, mutationOpRef, op)) return;
          next = revokedLocalShare(share);
          replaceItem(next);
          setConfirm(null);
          setRestoreExpiryError(null);
          return;
        }
        if (action === "restore_expired" || action === "reactivate") {
          next = await reactivateCompanyPresentation(
            ctx.companyId,
            share.id,
            withCorporateConcurrency(ctx, share, restoreBody || {}),
          );
        }
        if (action === "rotate") {
          next = await rotateCompanyPresentation(
            ctx.companyId,
            share.id,
            withCorporateConcurrency(ctx, share),
          );
        }
        if (action === "handover") {
          next = await handoverCompanyPresentation(
            ctx.companyId,
            share.id,
            withCorporateConcurrency(ctx, share, {
              responsible_user_id: Number(handoverUserId),
            }),
          );
        }
        if (action === "maintain_contact") {
          next = await maintainCompanyPresentationContact(
            ctx.companyId,
            share.id,
            toCorporateContactBody(ctx, share, contactDraft),
          );
        }
      } else {
        if (action === "revoke") next = await revokePropertyPresentation(propertyId, share.id);
        if (action === "reactivate") next = await reactivatePropertyPresentation(propertyId, share.id);
        if (action === "restore_expired") {
          next = await reactivatePropertyPresentation(propertyId, share.id, restoreBody);
        }
        if (action === "rotate") next = await rotatePropertyPresentation(propertyId, share.id);
      }
      if (!isCurrent(session, mutationOpRef, op)) return;
      if (next) replaceItem(next);
      if (action === "rotate") {
        setCopied(false);
        setQrOpen(false);
        setView("share");
      }
      setConfirm(null);
      setRestoreExpiryError(null);
      setContactDraft(null);
      setContactFieldErrors({});
    } catch (error) {
      if (!isCurrent(session, mutationOpRef, op)) return;
      if (companyMode && isCorporateConflictError(error)) {
        setShareFeedback(messageFromCorporateError(error));
        setConfirm(null);
        await refetchCorporateList(session);
        return;
      }
      if (action === "restore_expired") {
        const parsed = parseDrfFieldErrors(error, ["expires_at"]);
        if (parsed.expires_at) setRestoreExpiryError(parsed.expires_at);
        setShareFeedback(
          parsed._form
            || (!parsed.expires_at
              ? (companyMode ? messageFromCorporateError(error) : messageFromError(error))
              : null),
        );
      } else if (action === "maintain_contact") {
        const parsed = parseDrfFieldErrors(error, ["fallback_contact_name"]);
        const nextFields = { ...parsed };
        const form = nextFields._form || null;
        delete nextFields._form;
        setContactFieldErrors(nextFields);
        setShareFeedback(
          form
            || (Object.keys(nextFields).length
              ? null
              : (companyMode ? messageFromCorporateError(error) : messageFromError(error))),
        );
      } else {
        setShareFeedback(
          companyMode ? messageFromCorporateError(error) : messageFromError(error),
        );
      }
    } finally {
      if (isCurrent(session, mutationOpRef, op)) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };

  const refreshAnalytics = async (share) => {
    const session = sessionRef.current;
    const op = analyticsOpRef.current + 1;
    analyticsOpRef.current = op;
    try {
      const data = companyMode
        ? await fetchCompanyPresentationAnalytics(
            managerContextRef.current.companyId,
            share.id,
          )
        : await fetchPropertyPresentationAnalytics(propertyId, share.id);
      if (!isCurrent(session, analyticsOpRef, op)) return;
      replaceItem({ ...share, ...data });
    } catch (_err) {
      /* best-effort */
    }
  };

  const previewProjection = pickPublicProjection(
    view === "share" ? activeShare : view === "preview" ? previewShare : null,
  );
  const visibleItems =
    loadState === "loaded" && loadedPropertyId === propertyId ? items : [];
  const toolbarShare = view === "share" ? activeShare : view === "preview" ? previewShare : null;
  const managerTitle =
    view === "editor" || view === "review"
      ? editingShare
        ? "Редагування презентації"
        : "Створення презентації"
      : "Презентації для клієнтів";

  const modal = (
    <div
      ref={modalRef}
      className="presentation-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="presentation-manager-title"
      data-testid="presentation-manager"
      data-property-id={propertyId}
      data-manager-mode={companyMode ? "company" : "personal"}
    >
      <div className="presentation-modal__backdrop" onClick={onClose} />
      <div className="presentation-modal__dialog">
        <header
          className={
            view === "list"
              ? "presentation-modal__head presentation-modal__head--manager-list"
              : "presentation-modal__head"
          }
        >
          {view === "list" ? (
            <>
              <div className="presentation-modal__title-row">
                <h2 id="presentation-manager-title">{managerTitle}</h2>
                <button
                  ref={closeBtnRef}
                  type="button"
                  className="presentation-modal__close"
                  onClick={onClose}
                  aria-label="Закрити"
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                </button>
              </div>
              {canCreate ? (
              <div className="presentation-modal__create-row">
                <button
                  type="button"
                  className="btn-more presentation-modal__create"
                  onClick={startCreate}
                >
                  Нова презентація
                </button>
              </div>
              ) : null}
            </>
          ) : (
            <>
              <h2 id="presentation-manager-title">{managerTitle}</h2>
              <div className="presentation-modal__head-actions">
                <button
                  ref={closeBtnRef}
                  type="button"
                  className="presentation-modal__close"
                  onClick={onClose}
                  aria-label="Закрити"
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                </button>
              </div>
            </>
          )}
        </header>
        <div className="presentation-modal__body">
          {view === "list" ? (
            <>
              {loadState === "loading" ? <Spinner size="sm" label="Завантаження презентацій" /> : null}
              {loadState === "error" ? (
                <p className="presentation-form__error" role="alert">
                  {loadError}
                </p>
              ) : null}
              {loadState === "loaded" && visibleItems.length === 0 ? (
                <p>Ще немає презентацій для цього об'єкта.</p>
              ) : null}
              {visibleItems.length ? (
                <ul className="presentation-manager-list">
                  {visibleItems.map((share) => {
                    const status = presentationStatus(share);
                    const canUpdate = corporateActionAllowed(companyMode, share, "can_update");
                    const canRevoke = corporateActionAllowed(companyMode, share, "can_revoke");
                    const canReactivate = corporateActionAllowed(
                      companyMode,
                      share,
                      "can_reactivate",
                    );
                    const canRotate = corporateActionAllowed(companyMode, share, "can_rotate");
                    const canHandover = corporateActionAllowed(companyMode, share, "can_handover");
                    const canContact = corporateActionAllowed(
                      companyMode,
                      share,
                      "can_maintain_contact",
                    );
                    return (
                      <li key={share.id} className="presentation-manager-card">
                        <div className="presentation-manager-card__main">
                          <strong className="presentation-manager-card__title">
                            {share.internal_label || share.title || "Без назви"}
                          </strong>
                          <span className="presentation-manager-list__meta">
                            {share.client?.name || "Без прив'язки"}
                          </span>
                          {companyMode ? <PresentationBrandSnapshot share={share} /> : null}
                          <span className="presentation-manager-card__badges">
                            <span className={`presentation-status presentation-status--${status}`}>
                              {STATUS_LABELS[status]}
                            </span>
                            <span className="presentation-manager-list__meta">
                              до {formatDate(share.expires_at) || "—"}
                            </span>
                            <span className="presentation-manager-list__meta">
                              {PRICE_MODE_LABELS[share.price_mode] || share.price_mode}
                            </span>
                          </span>
                          <span className="presentation-manager-list__meta presentation-manager-card__stats">
                            Перегляди: {share.unique_views ?? 0}
                            {share.last_viewed_at
                              ? ` · востаннє ${formatDateTime(share.last_viewed_at)}`
                              : ""}
                          </span>
                        </div>
                        <div className="presentation-manager-list__actions">
                          <button
                            type="button"
                            className="action-btn"
                            onClick={() => {
                              setPreviewShare(share);
                              setActiveShare(share);
                              setView("preview");
                              if (companyMode) loadAudit(share);
                            }}
                          >
                            Перегляд
                          </button>
                          {share.public_url ? (
                            <>
                              <button type="button" className="action-btn" onClick={() => handleCopy(share)}>
                                Копіювати
                              </button>
                              <button type="button" className="action-btn" onClick={() => handleShare(share)}>
                                Надіслати
                              </button>
                              <button
                                type="button"
                                className="action-btn"
                                onClick={() => {
                                  setActiveShare(share);
                                  setQrOpen(true);
                                }}
                              >
                                QR
                              </button>
                            </>
                          ) : null}
                          {canUpdate ? (
                            <button type="button" className="action-btn" onClick={() => startEdit(share)}>
                              Редагувати
                            </button>
                          ) : null}
                        </div>
                        <div className="presentation-manager-list__lifecycle">
                          {status === "active" ? (
                            canRevoke ? (
                              <button
                                type="button"
                                className="action-btn action-btn--danger"
                                onClick={() =>
                                  setConfirm({
                                    action: "revoke",
                                    share,
                                    title: "Відкликати презентацію?",
                                    message: "Клієнт одразу втратить доступ за цим посиланням.",
                                    confirmLabel: "Відкликати",
                                    danger: true,
                                  })
                                }
                              >
                                Відкликати
                              </button>
                            ) : null
                          ) : presentationRequiresExpiryRestore(share) || companyMode ? (
                            canReactivate ? (
                              <button
                                type="button"
                                className="action-btn"
                                data-testid="presentation-restore-expired"
                                onClick={() => beginExpiredRestore(share)}
                              >
                                {companyMode ? "Відновити" : "Поновити термін"}
                              </button>
                            ) : null
                          ) : canReactivate ? (
                            <button
                              type="button"
                              className="action-btn"
                              onClick={() =>
                                setConfirm({
                                  action: "reactivate",
                                  share,
                                  title: "Відновити презентацію?",
                                  message: "Посилання знову стане доступним для клієнта.",
                                  confirmLabel: "Відновити",
                                })
                              }
                            >
                              Відновити
                            </button>
                          ) : null}
                          {canRotate ? (
                            <button
                              type="button"
                              className="action-btn action-btn--danger"
                              onClick={() =>
                                setConfirm({
                                  action: "rotate",
                                  share,
                                  title: "Оновити посилання?",
                                  message:
                                    "Старе посилання і QR перестануть працювати. Нове з'явиться після підтвердження.",
                                  confirmLabel: "Оновити посилання",
                                  danger: true,
                                })
                              }
                            >
                              Оновити посилання
                            </button>
                          ) : null}
                          {canHandover ? (
                            <button
                              type="button"
                              className="action-btn"
                              data-testid="presentation-handover"
                              onClick={() => beginHandover(share)}
                            >
                              Передати відповідальність
                            </button>
                          ) : null}
                          {canContact ? (
                            <button
                              type="button"
                              className="action-btn"
                              data-testid="presentation-maintain-contact"
                              onClick={() => beginContactMaintain(share)}
                            >
                              Оновити контакт
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="action-btn"
                            onClick={() => refreshAnalytics(share)}
                          >
                            Оновити статистику
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
              {copied ? <p role="status">Посилання скопійовано</p> : null}
              {shareFeedback ? (
                <p className="presentation-form__error" role="alert">
                  {shareFeedback}
                </p>
              ) : null}
            </>
          ) : null}

          {view === "editor" && draft ? (
            <PresentationEditorForm
              draft={draft}
              onChange={(next) => {
                setReviewPayload(null);
                setFieldErrors((prev) => {
                  if (!prev || !Object.keys(prev).length) return prev;
                  const cleared = { ...prev };
                  let changed = false;
                  Object.keys(prev).forEach((key) => {
                    if ((draft?.[key] ?? undefined) !== (next?.[key] ?? undefined)) {
                      delete cleared[key];
                      changed = true;
                    }
                  });
                  return changed ? cleared : prev;
                });
                setDraft(next);
              }}
              property={property}
              ownerContactPrefill={ownerContactPrefill}
              priceLocked={partnerPriceLocked}
              fieldErrors={fieldErrors}
              formError={formError}
              busy={busy}
              submitLabel="Далі до перевірки"
              stageIndex={editorStage}
              onStageIndexChange={setEditorStage}
              onCancel={() => setView("list")}
              onSubmit={enterReview}
              searchClients={companyMode ? managerContext?.searchClients : null}
              corporateMode={companyMode}
              isEditing={Boolean(editingShare)}
            />
          ) : null}

          {view === "review" && draft && reviewPayload ? (
            <div className="presentation-review-pane">
              <PresentationReviewSummary
                draft={draft}
                payload={reviewPayload}
                property={property}
              />
              <footer className="presentation-modal__foot presentation-editor__nav">
                <button
                  type="button"
                  className="action-btn"
                  onClick={() => {
                    setReviewPayload(null);
                    setView("editor");
                  }}
                  disabled={busy}
                >
                  Назад до редагування
                </button>
                <button
                  type="button"
                  className="action-btn action-btn--primary"
                  onClick={handleSave}
                  disabled={busy}
                >
                  {busy ? "Збереження…" : editingShare ? "Зберегти" : "Створити"}
                </button>
              </footer>
            </div>
          ) : null}

          {view === "preview" || view === "share" ? (
            <div className="presentation-share-success">
              {view === "share" && activeShare ? (
                <p>Презентація готова. Надішліть клієнту лише це посилання.</p>
              ) : null}
              {companyMode && toolbarShare ? (
                <PresentationBrandSnapshot share={toolbarShare} />
              ) : null}
              <PresentationOwnerToolbar
                share={toolbarShare}
                copied={copied}
                onCopy={handleCopy}
                onShare={handleShare}
                onQr={() => setQrOpen(true)}
                onEdit={
                  toolbarShare
                  && corporateActionAllowed(companyMode, toolbarShare, "can_update")
                    ? () => startEdit(toolbarShare)
                    : null
                }
                onList={() => setView("list")}
              />
              {toolbarShare ? <PresentationUrlRow url={toolbarShare.public_url} /> : null}
              {shareFeedback ? (
                <p className="presentation-form__error" role="alert">
                  {shareFeedback}
                </p>
              ) : null}
              {copied && view !== "list" ? <p role="status">Посилання скопійовано</p> : null}
              {previewProjection ? (
                <div className="presentation-owner-preview" data-testid="presentation-owner-preview">
                  <p className="presentation-owner-preview__label">Попередній перегляд</p>
                  <PublicPresentationRenderer
                    presentation={previewProjection}
                    footer={false}
                    variant="ownerPreview"
                  />
                </div>
              ) : null}
              {companyMode && view === "preview" ? (
                <section
                  className="presentation-audit-panel"
                  data-testid="presentation-audit-panel"
                  aria-label="Історія змін"
                >
                  <h3>Історія змін</h3>
                  {auditState === "loading" ? (
                    <Spinner size="sm" label="Завантаження історії" />
                  ) : null}
                  {auditState === "error" ? (
                    <p className="presentation-form__error" role="alert">
                      Не вдалося завантажити історію.
                    </p>
                  ) : null}
                  {auditState === "loaded" && auditRows.length === 0 ? (
                    <p>Подій ще немає.</p>
                  ) : null}
                  {auditRows.length ? (
                    <ul className="presentation-audit-list">
                      {auditRows.map((row, index) => (
                        <li key={row.id || `${row.action}-${index}`}>
                          <strong>{corporateAuditLabel(row.action)}</strong>
                          {row.created_at ? (
                            <span className="presentation-manager-list__meta">
                              {" "}
                              · {formatDateTime(row.created_at)}
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </section>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <PresentationConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
        busy={busy}
        errorMessage={shareFeedback}
        dialogClassName={
          confirm?.action === "restore_expired"
            || confirm?.action === "handover"
            || confirm?.action === "maintain_contact"
            ? "presentation-modal__dialog--restore"
            : undefined
        }
        onCancel={() => {
          if (busy) return;
          setConfirm(null);
          setRestoreExpiryError(null);
          setContactDraft(null);
          setHandoverUserId("");
        }}
        onConfirm={() => confirm && runLifecycle(confirm.action, confirm.share)}
      >
        {confirm?.action === "restore_expired" ? (
          <div className="presentation-restore-expiry" data-testid="presentation-restore-expiry">
            <Field
              id="presentation-restore-expiry-preset"
              label="Новий термін дії"
              help="Не більше 30 днів від сьогодні."
              error={restoreExpiryError}
              required
            >
              <Select
                value={restoreExpiryPreset}
                options={EXPIRY_PRESETS}
                onChange={(event) => {
                  setRestoreExpiryPreset(event.target.value);
                  setRestoreExpiryError(null);
                }}
              />
            </Field>
            {restoreExpiryPreset === "custom" ? (
              <Field id="presentation-restore-expiry-custom" label="Дата завершення">
                <TextInput
                  type="datetime-local"
                  value={restoreCustomExpiresAt}
                  onChange={(event) => {
                    setRestoreCustomExpiresAt(event.target.value);
                    setRestoreExpiryError(null);
                  }}
                />
              </Field>
            ) : null}
          </div>
        ) : null}
        {confirm?.action === "handover" ? (
          <div data-testid="presentation-handover-form">
            <Field
              id="presentation-handover-user"
              label="Новий відповідальний"
              required
            >
              <Select
                value={handoverUserId}
                options={[
                  { value: "", label: "Оберіть учасника" },
                  ...memberOptions,
                ]}
                onChange={(event) => setHandoverUserId(event.target.value)}
              />
            </Field>
          </div>
        ) : null}
        {confirm?.action === "maintain_contact" && contactDraft ? (
          <div data-testid="presentation-contact-form">
            <Field id="presentation-maintain-phone" label="Телефон">
              <TextInput
                type="tel"
                value={contactDraft.contact_phone}
                onChange={(event) =>
                  setContactDraft({ ...contactDraft, contact_phone: event.target.value })
                }
              />
            </Field>
            <Field id="presentation-maintain-email" label="Email">
              <TextInput
                type="email"
                value={contactDraft.contact_email}
                onChange={(event) =>
                  setContactDraft({ ...contactDraft, contact_email: event.target.value })
                }
              />
            </Field>
            <Field
              id="presentation-maintain-fallback-name"
              label="Запасне ім'я контакту"
              error={contactFieldErrors.fallback_contact_name || null}
            >
              <TextInput
                value={contactDraft.fallback_contact_name}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setContactFieldErrors((prev) => {
                    if (!prev.fallback_contact_name) return prev;
                    const next = { ...prev };
                    delete next.fallback_contact_name;
                    return next;
                  });
                  setContactDraft((prev) => ({
                    ...prev,
                    fallback_contact_name: nextValue,
                    dirtyFallbackFields: {
                      ...(prev.dirtyFallbackFields || {}),
                      fallback_contact_name: true,
                    },
                  }));
                }}
              />
            </Field>
            <PresentationToggle
              id="presentation-maintain-avatar"
              label="Показувати фото профілю"
              checked={Boolean(contactDraft.include_contact_avatar)}
              onChange={(checked) =>
                setContactDraft({ ...contactDraft, include_contact_avatar: checked })
              }
            />
            <PresentationToggle
              id="presentation-maintain-fallback"
              label="Підтвердити контакт компанії як запасний"
              checked={Boolean(contactDraft.confirm_company_fallback)}
              onChange={(checked) =>
                setContactDraft({ ...contactDraft, confirm_company_fallback: checked })
              }
            />
          </div>
        ) : null}
      </PresentationConfirmDialog>

      {qrOpen && activeShare?.public_url ? (
        <Suspense fallback={<Spinner size="sm" label="QR" />}>
          <PresentationQrPanel
            publicUrl={activeShare.public_url}
            onClose={() => setQrOpen(false)}
          />
        </Suspense>
      ) : null}
    </div>
  );

  return createPortal(modal, document.body);
}

export default PresentationManagerModal;
